import { useCallback } from "react";

import {
  ApiService,
  Case,
  CaseAdmin,
  CaseReportSignature,
  CliaCertificate,
  Icd10Code,
  InsurancePlan,
  Lab,
  User,
  UserList,
} from "pwapi";

import { InsuranceListItem } from "shared/components/AsyncListItems";
import { StainsState } from "shared/reducers/frontLoaderReducers";

import {
  ClinicOption,
  LabOption,
  PatientOption,
  UserOption,
  CaseOption,
  LabStainOption,
  StainOption,
  CliaCertificateOption,
  CaseReportSignatureOption,
  Icd10Option,
} from "./ResourceOptions";
import SelectSearchInput from "./SelectSearchInput";
import { LabStainValue } from "../types/adminTypes";

export const mapUsersToOptions = (users: User[]) => {
  return users
    .filter((u) => `${u.first_name}${u.last_name}`.length)
    .map((u) => ({
      value: u.id,
      label: `${u.first_name} ${u.last_name}`,
      object: u,
    }));
};

export const mapSignaturesToOptions = (signatures: CaseReportSignature[]) => {
  return signatures
    .filter((s) => s?.clia_certificate?.number)
    .map((s) => ({
      value: s.id,
      label: s?.clia_certificate?.number,
      object: s,
    }));
};

export const mapCasesToOptions = (cases: CaseAdmin[]) => {
  return cases.map((c) => ({
    value: c.id,
    label: `${c.accession}`,
    object: c,
  }));
};

export const mapIcd10ToOptions = (icd10Codes: Icd10Code[]) => {
  return icd10Codes.map((c) => ({
    value: c.id,
    label: c.code,
    object: c,
  }));
};

export const mapLabsToOptions = (labs: Lab[]) => {
  return labs.map((l) => ({
    value: l.id,
    label: l.name,
    object: l,
  }));
};

export const genericMapToOptions = (objects: any[]) => {
  return objects.map((o) => ({
    value: o.id,
    label: o.name,
    object: o,
  }));
};

export const AdminFieldPatient = ({
  onChange,
  value,
  disabled = false,
}: {
  onChange: Function;
  value: CaseAdmin["patient"];
  disabled?: boolean;
}) => {
  return (
    <SelectSearchInput
      label="Patient"
      field="patient"
      filter="name"
      onChange={onChange}
      mapToOptions={mapUsersToOptions}
      apiService={ApiService.listPatients}
      component={PatientOption}
      disabled={disabled}
    >
      {value ? (
        <PatientOption object={value} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          Missing Patient
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldActiveAssignment = ({
  onChange,
  value,
  disabled = false,
}: {
  onChange: Function;
  value: CaseAdmin["active_assignment"];
  disabled?: boolean;
}) => {
  return (
    <SelectSearchInput
      label="Active Assignment"
      field="active_assignment"
      filter="name"
      onChange={onChange}
      mapToOptions={mapUsersToOptions}
      apiService={ApiService.listUsers}
      component={UserOption}
      disabled={disabled}
    >
      {value ? (
        <UserOption object={value as UserList} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          Unassigned
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldSignature = ({
  onChange,
  value,
  label,
  field,
  missingMessage,
  useIsMissingInfo,
  disabled = false,
  required = false,
  warning,
  serviceOptions,
}: {
  onChange: Function;
  value?: CaseReportSignature;
  label: string;
  field: string;
  missingMessage?: string;
  useIsMissingInfo?: boolean;
  disabled?: boolean;
  required?: boolean;
  warning?: string;
  serviceOptions?: { [key: string]: any };
}) => {
  return (
    <SelectSearchInput
      label={label}
      field={field}
      filter="nameOrDescription"
      onChange={onChange}
      mapToOptions={mapSignaturesToOptions}
      apiService={ApiService.listCaseReportSignatures}
      component={CaseReportSignatureOption}
      serviceOptions={serviceOptions}
      isMissingInfo={useIsMissingInfo && !value}
      disabled={disabled}
      required={required}
      requiredFill={value?.id}
      warning={!!warning}
    >
      {value ? (
        <CaseReportSignatureOption object={value} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          {missingMessage || "Unassigned"}
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldAssignment = ({
  onChange,
  value,
  label,
  field,
  missingMessage,
  useIsMissingInfo,
  disabled = false,
}: {
  onChange: Function;
  value: User;
  label: string;
  field: string;
  missingMessage?: string;
  useIsMissingInfo?: boolean;
  disabled?: boolean;
}) => {
  return (
    <SelectSearchInput
      label={label}
      field={field}
      filter="name"
      onChange={onChange}
      mapToOptions={mapUsersToOptions}
      apiService={ApiService.listUsers}
      component={UserOption}
      isMissingInfo={useIsMissingInfo && !value}
      disabled={disabled}
    >
      {value ? (
        <UserOption object={value} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          {missingMessage || "Unassigned"}
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldOrderingDoctor = ({
  onChange,
  onClear,
  value,
  patientClinicId,
  disabled = false,
  required = false,
  error,
  warning,
}: {
  onChange: Function;
  onClear: Function;
  value: CaseAdmin["ordering_doctor"];
  patientClinicId?: number;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  warning?: string;
}) => {
  return (
    <SelectSearchInput
      label="Ordering Provider"
      field="ordering_doctor"
      filter="name"
      onChange={onChange}
      mapToOptions={mapUsersToOptions}
      apiService={ApiService.listUsers}
      serviceOptions={{
        userprofileUserType: ["physician", "dermpath"],
        clinicId: patientClinicId,
      }}
      component={UserOption}
      disabled={disabled}
      required={required}
      requiredFill={value?.id}
      isMissingInfo={!!error}
      warning={!!warning}
      value={value}
      onClear={onClear}
    >
      {value ? (
        <UserOption object={value as UserList} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          Missing Ordering Provider
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldSupervisingPhysician = ({
  onChange,
  onClear,
  value,
  patientClinicId,
  disabled = false,
  required = false,
  error,
  warning,
}: {
  onChange: Function;
  onClear: Function;
  value: CaseAdmin["supervising_physician"];
  patientClinicId?: number;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  warning?: string;
}) => {
  return (
    <SelectSearchInput
      label="Supervising Physician"
      field="supervising_physician"
      filter="name"
      onChange={onChange}
      mapToOptions={mapUsersToOptions}
      apiService={ApiService.listUsers}
      serviceOptions={{
        userprofileUserType: ["physician", "dermpath"],
        clinicId: patientClinicId,
      }}
      component={UserOption}
      disabled={disabled}
      required={required}
      requiredFill={value?.id}
      isMissingInfo={!!error}
      warning={!!warning}
      value={value}
      onClear={onClear}
    >
      {value ? (
        <UserOption object={value as UserList} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          Add Supervising Physician
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldLab = ({
  onChange,
  value,
  label,
  disabled = false,
  required = false,
}: {
  onChange: Function;
  value: Lab;
  label: string;
  disabled?: boolean;
  required?: boolean;
}) => {
  return (
    <SelectSearchInput
      label={label}
      field="lab"
      filter="name"
      onChange={onChange}
      mapToOptions={mapLabsToOptions}
      apiService={ApiService.listLabs}
      serviceOptions={{ userprofileUserType: "physician" }}
      component={LabOption}
      disabled={disabled}
      required={required}
      requiredFill={value?.id}
    >
      {value ? <LabOption object={value} /> : `Missing ${label}`}
    </SelectSearchInput>
  );
};

export const AdminFieldClinic = ({
  onChange,
  onClear,
  value,
  disabled = false,
  required = false,
  error,
  serviceOptions,
}: {
  onChange: Function;
  onClear: Function;
  value?: any;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  serviceOptions?: {
    case_source?: Case.source;
    is_archived?: boolean;
  };
  case_source?: Case.source;
}) => {
  return (
    <SelectSearchInput
      label="Clinic"
      field="clinic"
      filter="name"
      onChange={onChange}
      mapToOptions={genericMapToOptions}
      apiService={ApiService.listClinics}
      serviceOptions={serviceOptions}
      component={ClinicOption}
      disabled={disabled}
      required={required}
      requiredFill={value?.id}
      isMissingInfo={!!error}
      value={value}
      onClear={onClear}
    >
      {value ? (
        <ClinicOption object={value} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          No Clinic Assigned
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldCase = ({
  onChange,
  value,
  disabled = false,
  required = false,
}: {
  onChange: Function;
  value: CaseAdmin;
  disabled?: boolean;
  required?: boolean;
}) => {
  return (
    <SelectSearchInput
      label="Case"
      field="case"
      filter="accession"
      onChange={onChange}
      mapToOptions={mapCasesToOptions}
      apiService={ApiService.listAdminCases}
      component={CaseOption}
      disabled={disabled}
      required={required}
      requiredFill={value?.id}
    >
      {value?.id ? (
        <CaseOption object={value} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          Missing Case
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldLabStainType = ({
  onChange,
  value,
  availableStains = {},
  disabled = false,
  required = false,
}: {
  onChange: Function;
  value?: number;
  availableStains: Record<string, LabStainValue>;
  disabled?: boolean;
  required?: boolean;
}) => {
  const fakeApiService = useCallback(
    ({ name }: { name?: string }) => {
      const results = name
        ? Object.values(availableStains).filter(
            (s) =>
              s.name?.toLowerCase().startsWith(name.toLowerCase()) ||
              s.abbreviation?.toLowerCase().startsWith(name.toLowerCase())
          )
        : Object.values(availableStains);
      return Promise.resolve({
        results: results.slice(0, 20),
      });
    },
    [availableStains]
  );
  const stain = availableStains[value || 0];

  return (
    <SelectSearchInput
      label="Stain"
      field="stain"
      filter="name"
      onChange={onChange}
      mapToOptions={genericMapToOptions}
      apiService={fakeApiService}
      component={LabStainOption}
      disabled={disabled}
      required={required}
      requiredFill={value}
    >
      {value ? (
        <LabStainOption object={stain} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          Missing Stain
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldStainType = ({
  onChange,
  value,
  allStains = [],
  availableStains = [],
  disabled = false,
  required = false,
}: {
  onChange: Function;
  value?: number;
  allStains: StainsState;
  availableStains: StainsState;
  disabled?: boolean;
  required?: boolean;
}) => {
  const fakeApiService = useCallback(
    ({ name }: { name?: string }) => {
      const results = name
        ? Object.values(availableStains).filter(
            (s) =>
              s.name?.toLowerCase().startsWith(name.toLowerCase()) ||
              s.abbreviation?.toLowerCase().startsWith(name.toLowerCase())
          )
        : Object.values(availableStains);
      return Promise.resolve({
        results: results.slice(0, 20),
      });
    },
    [availableStains]
  );
  const stain = allStains[value || 0];

  return (
    <SelectSearchInput
      label="Stain"
      field="stain"
      filter="name"
      onChange={onChange}
      mapToOptions={genericMapToOptions}
      apiService={fakeApiService}
      component={StainOption}
      disabled={disabled}
      required={required}
      requiredFill={value}
    >
      {value ? (
        <StainOption object={stain} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          Missing Stain
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldGrossedByCert = ({
  onChange,
  value,
  labName,
  disabled = false,
  required = false,
}: {
  onChange: Function;
  value: CliaCertificate;
  labName?: string;
  disabled?: boolean;
  required?: boolean;
}) => {
  return (
    <SelectSearchInput
      label="TC CLIA"
      field="grossed_by_cert"
      filter="name"
      onChange={onChange}
      mapToOptions={genericMapToOptions}
      apiService={ApiService.listCliaCertificates}
      serviceOptions={{
        labName: labName,
        hasLabId: true,
      }}
      component={CliaCertificateOption}
      disabled={disabled}
      required={required}
      requiredFill={value?.id}
    >
      {value ? (
        <CliaCertificateOption object={value as CliaCertificate} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          Missing Grossed By Certificate
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldInsurancePlan = ({
  onChange,
  value,
  disabled = false,
  required = false,
}: {
  onChange: Function;
  value: InsurancePlan;
  disabled?: boolean;
  required?: boolean;
}) => {
  return (
    <SelectSearchInput
      label="Insurance Plan"
      field="insurance_plan"
      filter="searchTerm"
      onChange={onChange}
      mapToOptions={genericMapToOptions}
      apiService={ApiService.listAdminInsurancePlans}
      component={InsuranceListItem}
      disabled={disabled}
      debounceTime={500}
      inputLimit={3}
      required={required}
      requiredFill={value?.id}
    >
      {value ? (
        <InsuranceListItem object={value} />
      ) : (
        <div className="AdminEdit__object AdminEdit__object--missing">
          Missing Insurance Plan
        </div>
      )}
    </SelectSearchInput>
  );
};

export const AdminFieldIcd10Code = ({
  value,
  onChange,
  idx,
  disabled = false,
}: {
  value: Icd10Code;
  onChange: Function;
  idx?: number;
  disabled?: boolean;
}) => {
  return (
    <SelectSearchInput
      id={`icd10_code${idx}`}
      field={`icd10_code[${idx}]`}
      component={Icd10Option}
      mapToOptions={mapIcd10ToOptions}
      disabled={disabled}
      apiService={(args: Parameters<typeof ApiService.listIcd10Codes>[0]) =>
        ApiService.listIcd10Codes(args)
      }
      filter="code"
      onChange={(newValue: {
        label: string;
        object: Icd10Code;
        value: number;
      }) => onChange(newValue.object, idx)}
    >
      {value.code ? value.code : "Select an ICD-10 code..."}
    </SelectSearchInput>
  );
};
